/**
 * 仓库管理 - 常量定义
 * 
 * 本文件定义仓库相关的常量映射和配置
 * 
 * @module types/warehouse/constants
 * @category Warehouse - 仓库管理
 * 
 * @description
 * 包含的常量：
 * - WAREHOUSE_TYPE_LABELS: 仓库类型中文标签映射
 * - WAREHOUSE_STATUS_LABELS: 仓库状态中文标签映射
 * - WAREHOUSE_PERMISSIONS: 仓库管理权限常量
 * 
 * @backend-mapping
 * 对应后端文件：
 * - Model: backend/app/Modules/Warehouse/Models/Warehouse.php
 *   - getTypes() → WAREHOUSE_TYPE_LABELS
 *   - STATUS_* 常量 → WAREHOUSE_STATUS_LABELS
 * - Seeder: backend/database/seeders/Permissions/WarehousePermissionSeeder.php
 *   - → WAREHOUSE_PERMISSIONS
 * 
 * @field-naming
 * - 常量名：UPPER_SNAKE_CASE（如 WAREHOUSE_TYPE_LABELS）
 * - 对象键：枚举值（如 'RAW'）
 * - 对象值：中文标签（如 '原料仓'）
 * 
 * @important
 * ⚠️ 标签文本必须与后端 getTypes() 等方法返回的中文完全一致
 * ⚠️ 不要自行修改标签文本，以保持前后端一致性
 * 
 * @update-trigger
 * 当后端发生以下变更时需要同步更新：
 * - Model 的 getTypes() 等方法返回值变化
 * - 新增/修改枚举类型
 * - PermissionSeeder 新增/修改权限
 * 
 * @validation
 * 验证方式：
 * 1. 对照 Model 的 getTypes() 方法检查标签
 * 2. 检查是否所有枚举都有对应标签
 * 3. 检查标签文本是否与后端一致
 * 4. 对照 PermissionSeeder 检查权限常量
 * 
 * @example
 * ```typescript
 * import { WAREHOUSE_TYPE_LABELS, WarehouseType } from '@bayes-mes/shared';
 * 
 * // 显示仓库类型标签
 * const label = WAREHOUSE_TYPE_LABELS[WarehouseType.RAW]; // '原料仓'
 * ```
 * 
 * @last-updated 2025-10-18
 * @author YSFL Team
 */

import { WarehouseType, WarehouseStatus } from './enums';

/**
 * 仓库类型中文标签映射
 * 
 * 对应后端：Warehouse::getTypes()
 */
export const WAREHOUSE_TYPE_LABELS: Record<WarehouseType, string> = {
  [WarehouseType.RAW]: '原料仓',
  [WarehouseType.SEMI]: '半成品仓',
  [WarehouseType.PRODUCT]: '成品仓',
  [WarehouseType.LINE]: '线边仓',
  [WarehouseType.REJECT]: '不良品仓',
};

/**
 * 仓库状态中文标签映射
 * 
 * 对应后端：Warehouse::getStatusNameAttribute()
 */
export const WAREHOUSE_STATUS_LABELS: Record<WarehouseStatus, string> = {
  [WarehouseStatus.INACTIVE]: '停用',
  [WarehouseStatus.ACTIVE]: '启用',
};

/**
 * ⚠️ 仓库管理权限常量已迁移至统一权限管理
 * 
 * 请从 @bayes-mes/shared 导入标准权限常量：
 * 
 * 仓库管理权限：
 * - PERM_WAREHOUSE_WAREHOUSE_VIEW
 * - PERM_WAREHOUSE_WAREHOUSE_CREATE
 * - PERM_WAREHOUSE_WAREHOUSE_UPDATE
 * - PERM_WAREHOUSE_WAREHOUSE_DELETE
 * - PERM_WAREHOUSE_WAREHOUSE_IMPORT
 * 
 * 库位管理权限：
 * - PERM_WAREHOUSE_LOCATION_VIEW
 * - PERM_WAREHOUSE_LOCATION_CREATE
 * - PERM_WAREHOUSE_LOCATION_UPDATE
 * - PERM_WAREHOUSE_LOCATION_DELETE
 * - PERM_WAREHOUSE_LOCATION_IMPORT
 * 
 * @deprecated 请使用 constants/api-permissions.ts 中的权限常量
 * @see bayes-mes-shared/constants/api-permissions.ts
 */

